import { Suspense, useDeferredValue, useState } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
    BlockStack,
    Box,
    PageHeader,
    SegmentedControl,
    TextField,
    type SegmentedOption,
} from '@pv-frontend/ui-components-web';
import { platformSummaryQuery } from '../relay/platform-summary-query';
import type { platformSummaryQuery as PlatformSummaryQuery } from '../relay/__generated__/platformSummaryQuery.graphql';
import { vaultSearchQuery } from '../relay/vault-search-query';
import type { vaultSearchQuery as VaultSearchQuery } from '../relay/__generated__/vaultSearchQuery.graphql';
import { TenantSummary, type PlatformSummaryView } from './tenant-summary';
import { VaultTable, type VaultView } from './vault-table';
import { LoadingContent } from './loading-content';
import { parseVaultListFilter, type VaultListFilterView } from './vault-list-filter';

/**
 * Tenant-visibility entry (slice 7): a cross-tenant summary dashboard plus a
 * searchable, read-only vault list. Suspends while the summary loads; the vault
 * results have their own Suspense boundary so the search field keeps focus (and
 * the header stays put) as the query re-runs on each keystroke.
 *
 * `?vaults=complimentary` (the summary's complimentary count links here) narrows the
 * list to every vault on a live complimentary grant, with its term end and granter.
 */
export function TenantsScreen() {
    const data = useLazyLoadQuery<PlatformSummaryQuery>(
        platformSummaryQuery,
        {},
        { fetchPolicy: 'store-or-network' },
    );

    const summary: PlatformSummaryView = {
        totalVaults: data.platformSummary.totalVaults,
        totalAccounts: data.platformSummary.totalAccounts,
        freeSubscriptions: data.platformSummary.freeSubscriptions,
        paidSubscriptions: data.platformSummary.paidSubscriptions,
        complimentarySubscriptions: data.platformSummary.complimentarySubscriptions,
        planBreakdown: data.platformSummary.planBreakdown.map((plan) => ({
            planName: plan.planName,
            subscriptionCount: plan.subscriptionCount,
            complimentaryCount: plan.complimentaryCount,
        })),
    };

    const filter = parseVaultListFilter(useSearch({ from: '/tenants' }));
    const navigate = useNavigate();
    const filterOptions: readonly SegmentedOption<VaultListFilterView>[] = [
        { value: 'all', label: 'All vaults' },
        {
            value: 'complimentary',
            label: `Complimentary (${summary.complimentarySubscriptions.toLocaleString()})`,
        },
    ];
    const setFilter = (next: VaultListFilterView) =>
        void navigate({
            to: '/tenants',
            search: next === 'complimentary' ? { vaults: 'complimentary' } : {},
        });

    const [search, setSearch] = useState('');
    // The server query trails the live input, so typing keeps the last results on
    // screen (React holds the stale tree) instead of flashing the loading state.
    const deferredSearch = useDeferredValue(search);

    return (
        <Box padding="600" maxWidth={1080}>
            <BlockStack gap="500">
                <PageHeader
                    title="Tenants"
                    description="Cross-tenant summary and vault directory from the Admin API. Read-only."
                />

                <TenantSummary summary={summary} />

                <BlockStack gap="300">
                    <SegmentedControl
                        accessibilityLabel="Which vaults to list"
                        options={filterOptions}
                        value={filter}
                        onChange={setFilter}
                    />
                    <TextField
                        value={search}
                        onChange={setSearch}
                        accessibilityLabel="Search vaults by name, identifier or owner email"
                        placeholder="Search vaults…"
                        type="search"
                        leadingIcon="search"
                    />
                    <Suspense fallback={<LoadingContent />}>
                        <VaultResults query={deferredSearch} liveSearch={search} filter={filter} />
                    </Suspense>
                </BlockStack>
            </BlockStack>
        </Box>
    );
}

/**
 * Fetches the vault-search results for the (debounced) query and hands them to the
 * pure table. Kept separate from the search field so only this subtree suspends.
 *
 * ⚠️ The complimentary list always asks the server with a BLANK query: that is the
 * read selected in billing, so it holds every live comp (the unfiltered listing is capped).
 * The search field then narrows those rows client-side, so a typed term can never
 * hide a comp the summary counted.
 */
function VaultResults({
    query,
    liveSearch,
    filter,
}: {
    query: string;
    liveSearch: string;
    filter: VaultListFilterView;
}) {
    const complimentary = filter === 'complimentary';
    const data = useLazyLoadQuery<VaultSearchQuery>(
        vaultSearchQuery,
        { query: complimentary ? '' : query, filter: complimentary ? 'COMPLIMENTARY' : 'ALL' },
        { fetchPolicy: 'store-or-network' },
    );

    const vaults: VaultView[] = data.vaultSearch.map((vault) => ({
        vaultId: vault.vaultId,
        name: vault.name,
        identifier: vault.identifier,
        jurisdiction: vault.jurisdiction,
        ownerEmail: vault.ownerEmail ?? null,
        planName: vault.planName ?? null,
        subscriptionStatus: vault.subscriptionStatus ?? null,
        isComplimentary: vault.isComplimentary,
        complimentaryUntil: vault.complimentaryUntil ?? null,
        grantedBy: vault.grantedBy ?? null,
    }));

    return <VaultTable vaults={vaults} search={liveSearch} filter={filter} />;
}
